import { Box, Typography } from "@mui/material";
import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "@emotion/styled";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getImageForType } from "../../utils/cms.utils";
import ContentTag from "../../utils/tag";
import smoothScroll from "../../utils/animateScroll";
import { getAgeRangePublicRoute } from "../../utils/ageRange.util";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentRowScrollingWrapper = styled(Box)`
  overflow-x: auto;
  overflow-y: hidden;
`;

const ContentRowScrollingContent = styled(Box)`
  transition: all ease 0.5s;
`;

const ContentRowWrapper = styled(Box)`
  margin-bottom: 5rem;
  position: relative;
`;

const ContentRowTitle = styled(Typography)`
  color: white;
  margin-bottom: 22px;
  font-size: 27px;
  font-weight: 700;
  padding-right: 40px;
  padding-top: 40px;
`;

const MovieItem = ({ item = {}, onFocus }) => {
  const navigate = useNavigate();
  const { contentPhotos, tags, slug, contentType, _id } = item;

  const handleEnterPress = () => {
    navigate(`/${contentType?.kind}/${slug}`);
  };

  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: handleEnterPress,
    focusKey: "content_" + _id,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  return (
    <Box
      ref={ref}
      width={"270px"}
      minWidth={"215px"}
      minHeight={"350px"}
      p={2}
      mb={1}
      onMouseEnter={handleMouseEnter}
      onClick={handleEnterPress}
      sx={{ cursor: "pointer", position: "relative" }}
    >
      {/* Content card with background image */}
      <Box
        sx={{
          width: "100%",
          background: `url(${getImageForType("verticalCover", contentPhotos)})`,
          minHeight: "100%",
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px",
        }}
        className="rounded-2xl"
      >
        {tags?.[0] && <ContentTag label={tags?.[0]} />}
        {/* Focus overlay for title/year display */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            opacity: focused ? 1 : 0,
            pointerEvents: "none",
            transition: "opacity 0.2s",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: 2,
            flexDirection: "column",
          }}
        >
          <Typography
            fontFamily={"AnjomanMax"}
            sx={{
              color: "white",
              fontSize: "20px",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {item.title}
          </Typography>
          <Typography
            fontFamily={"AnjomanMax"}
            sx={{
              color: "white",
              fontSize: "16px",
              fontWeight: 400,
              textAlign: "center",
            }}
          >
            {item.year}
          </Typography>
        </Box>
      </Box>
      {/* Separate focus border layer - prevents background repeat */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          right: 8,
          bottom: 4,
          border: focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive,
          borderRadius: "16px",
          pointerEvents: "none",
          transition: "border 0.2s",
          zIndex: focused ? 10 : 0,
        }}
      />
    </Box>
  );
};

export const MoviesSlider = ({
  movies,
  onFocus,
  rowTitle,
  shouldFocusSelf,
}) => {
  const ageRange = getAgeRangePublicRoute();
  const _items = movies.filter(
    (x) =>
      x.content?.ageRange <= ageRange ||
      x.contentMeta?.content?.ageRange <= ageRange ||
      x.ageRange <= ageRange
  );
  const { ref, focusKey, focusSelf } = useFocusable({ onFocus });

  useEffect(() => {
    if (shouldFocusSelf === true) {
      focusSelf();
    }
  }, [shouldFocusSelf]);

  const scrollingRef = useRef(null);
  const { isMouseFocus } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      if (isMouseFocus) return;
      const { y, height } = props;

      const targetScroll = y > height - 200 ? y - 200 : 0;

      smoothScroll(scrollingRef, targetScroll, 500);
    },
    [scrollingRef, isMouseFocus]
  );
  if (_items.length < 1) return null;
  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref}>
        <ContentRowTitle>{rowTitle}</ContentRowTitle>
        <ContentRowScrollingWrapper ref={scrollingRef}>
          <ContentRowScrollingContent className="flex flex-wrap w-full">
            {_items?.map((item) => {
              return (
                <MovieItem item={item} key={item._id} onFocus={onAssetFocus} />
              );
            })}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
